import { useQuery } from '@apollo/client/react';
import styled from '@emotion/styled';
import { Duration, ZonedDateTime } from '@js-joda/core';
import { useAuth } from '@teamexos/fit-shared';
import { Button } from 'components/common/Button';
import { ModalContainer } from 'components/common/Modal';
import { InactivityProviderDocument } from 'graphql/types';
import useMixpanel from 'hooks/useMixpanel';
import React, { useCallback, useEffect, useState } from 'react';
import { IdleTimerProvider, useIdleTimerContext } from './IdleTimerContext';

const Wrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '326px',
  paddingLeft: theme.baseUnit * 2,
  paddingRight: theme.baseUnit * 2,
  paddingBottom: theme.baseUnit * 2,
  paddingTop: theme.baseUnit * 5,
  backgroundColor: theme.colors.alias.mainBackground,
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'center',
  borderRadius: theme.baseUnit,
}));

const Title = styled('h1')(({ theme }) => ({
  ...theme.typography.textVariant.title.s,
  color: theme.colors.alias.textPrimary,
  marginTop: 0,
  marginBottom: theme.baseUnit * 2,
}));

const Subtitle = styled('p')(({ theme }) => ({
  ...theme.typography.textVariant.subtitle.s,
  color: theme.colors.alias.textSecondary,
  marginTop: 0,
  marginBottom: theme.baseUnit * 5,
}));

const Prompt = ({
  open,
  promptBeforeIdleSeconds,
}: {
  open: boolean;
  promptBeforeIdleSeconds: number;
}) => {
  const [remaining, setRemaining] = useState<number>(0);

  const { activate, getRemainingTime } = useIdleTimerContext();

  const track = useMixpanel();

  useEffect(() => {
    if (!open) {
      setRemaining(0);
      return;
    }

    // Initialize remaining time immediately when prompt opens
    // If getRemainingTime returns 0 or suspiciously small value, use promptBeforeIdle as fallback
    const initialTime = getRemainingTime();
    const initialSeconds = Math.ceil(initialTime / 1000);
    setRemaining(initialSeconds > 0 ? initialSeconds : promptBeforeIdleSeconds);

    const interval = setInterval(() => {
      const time = getRemainingTime();
      const seconds = Math.ceil(time / 1000);
      // Only update if we get a valid value, otherwise keep the current value
      if (seconds > 0) {
        setRemaining(seconds);
      }
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [open, getRemainingTime, promptBeforeIdleSeconds]);

  return (
    <ModalContainer open={open}>
      <Wrapper>
        <Title>Are you still here?</Title>
        <Subtitle>Logging out in {remaining} seconds</Subtitle>
        <Button
          onClick={() => {
            activate();
            track('User Action', {
              event_category: 'sign-in',
              event_location: 'sign_out_modal',
              event_name: 'stay_signed_in',
              event_type: 'screen interaction',
            });
          }}
        >
          Yes, I&apos;m here
        </Button>
      </Wrapper>
    </ModalContainer>
  );
};

const INACTIVITY_TIME = '@inactivityTime';

const InactivityProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { signOut, isLoggedIn, isLoading: isAuthLoading } = useAuth();
  const { data } = useQuery(InactivityProviderDocument, {
    // skip until auth state is resolved; if isAuthLoading never settles,
    // inactivity tracking will not start (acceptable — user is likely unauthenticated)
    skip: !isLoggedIn || isAuthLoading,
  });
  const inactivityDuration = data?.self?.fitClient?.inactivityDuration
    ? Duration.parse(data.self.fitClient.inactivityDuration)
    : null;
  const clientUsesInactivityTracking = !!inactivityDuration;
  const promptBeforeIdle = Duration.ofMinutes(1);
  const track = useMixpanel();

  const [promptOpen, setPromptOpen] = useState(false);

  const doSignout = useCallback(() => {
    localStorage.removeItem(INACTIVITY_TIME);
    signOut(false);
  }, [signOut]);

  useEffect(() => {
    if (!isLoggedIn) {
      setPromptOpen(false);
      return;
    }
    const previousInactivityTime = localStorage.getItem(INACTIVITY_TIME);

    if (previousInactivityTime) {
      const inactivityTime = ZonedDateTime.parse(previousInactivityTime);
      const remainingTime = Duration.between(
        ZonedDateTime.now(),
        inactivityTime,
      );

      if (remainingTime.toMillis() <= 0) {
        doSignout();
      }
    }
  }, [isLoggedIn, doSignout]);

  return (
    <IdleTimerProvider
      timeout={inactivityDuration?.toMillis() ?? 0}
      promptBeforeIdle={promptBeforeIdle.toMillis()}
      stopOnIdle
      crossTab
      debounce={500}
      disabled={!clientUsesInactivityTracking || !isLoggedIn}
      onAction={(_, idleTimer) => {
        if ((idleTimer?.getRemainingTime() ?? 0) > 0) {
          localStorage.setItem(
            INACTIVITY_TIME,
            ZonedDateTime.now()
              .plusSeconds((idleTimer?.getRemainingTime() ?? 0) / 1000)
              .toString(),
          );
        }
      }}
      onIdle={() => {
        setPromptOpen(false);
        doSignout();
      }}
      onActive={() => {
        setPromptOpen(false);
      }}
      onPrompt={() => {
        track('Screen View', {
          page_name: 'sign_out_modal',
        });
        setPromptOpen(true);
      }}
    >
      <Prompt
        open={promptOpen}
        promptBeforeIdleSeconds={Math.floor(promptBeforeIdle.toMillis() / 1000)}
      />
      {children}
    </IdleTimerProvider>
  );
};

export default InactivityProvider;
